import { typography } from '@pixee/design-system'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Table } from '../../components/table/table.tsx'
import { Routes } from '../../routes.ts'
import { pluralize } from '../../utils/text-utils.ts'
import { RELEVANCE_LABELS } from '../../utils/threat-model/threat-coverage.ts'
import { CountDetailTooltip } from './count-detail-tooltip.tsx'
import type { VulnerabilityRecurrence } from './org-threat-posture.ts'
import * as styles from './security-posture-page.css.ts'

/**
 * How widely each vulnerability class appears across the estate.
 *
 * Uses the shared `Table` rather than markup of its own, so it inherits the app's row rhythm, header
 * treatment, hover, empty state and loading skeleton — the two tables on this page were hand-rolled and
 * had drifted from all of it.
 */
/** The shared `Table` puts `meta.className` on the cells but not the header, so the header centres itself. */
const centredHeader = (label: string) => () => <div className={styles.centredColumn}>{label}</div>

const columnHelper = createColumnHelper<VulnerabilityRecurrence>()

const columns = [
  columnHelper.accessor('name', {
    header: 'Vulnerability',
    cell: ({ row }) => row.original.name,
    meta: { width: '34%', className: typography.paragraph },
  }),
  columnHelper.accessor('strongestRelevance', {
    header: centredHeader('Strongest relevance'),
    cell: ({ row }) => (
      <span className={styles.relevanceTag[row.original.strongestRelevance]}>
        {RELEVANCE_LABELS[row.original.strongestRelevance]}
      </span>
    ),
    meta: { width: '22%', className: styles.centredColumn },
  }),
  columnHelper.accessor('repositoryCount', {
    header: centredHeader('Repositories'),
    cell: ({ row }) => <span className={styles.numericCell}>{row.original.repositoryCount}</span>,
    meta: { width: '14%', className: styles.centredColumn },
  }),
  columnHelper.accessor('defendedCount', {
    header: centredHeader('Defended'),
    cell: ({ row }) => <span className={styles.numericCell}>{row.original.defendedCount}</span>,
    meta: { width: '14%', className: styles.centredColumn },
  }),
  columnHelper.accessor('gapCount', {
    header: centredHeader('Coverage gaps'),
    cell: ({ row }) => (
      <CountDetailTooltip
        heading={`No control names ${row.original.name}`}
        triggerLabel={`${row.original.gapCount} ${pluralize(row.original.gapCount, 'repository', 'repositories')} with no control against ${row.original.name}`}
        items={row.original.gapRepositories.map(repository => ({
          key: repository.repositoryId,
          name: repository.name,
          href: `${Routes.RepositoryDetailsPage.createPath({ repositoryId: repository.repositoryId })}?tab=threat-model`,
        }))}
      >
        <span className={row.original.gapCount > 0 ? styles.gapCount : styles.numericCell}>
          {row.original.gapCount}
        </span>
      </CountDetailTooltip>
    ),
    meta: { width: '16%', className: styles.centredColumn },
  }),
] as ColumnDef<VulnerabilityRecurrence>[]

export const WidespreadVulnerabilitiesTable: React.FC<{ vulnerabilities: VulnerabilityRecurrence[] }> = ({
  vulnerabilities,
}) => (
  <Table
    tableName="Widespread vulnerabilities"
    data={vulnerabilities}
    columns={columns}
    getRowId={row => row.name}
    emptyStateMessage="No vulnerability classes were reported across the modelled repositories."
  />
)
